import { Core } from "@faire/design-tokens";
import { trackRetailerTeamManagementShareButtonClick } from "@faire/web-api/events/retailerTeamManagement/click/shareButton";
import { trackRetailerTeamManagementShareButtonView } from "@faire/web-api/events/retailerTeamManagement/view/shareButton";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ShareableItemType } from "@faire/web-api/indigofair/retailer/ShareableItemType";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { IconButton } from "@faire/web/slate/IconButton";
import { Share } from "@faire/web/slate/icons/foundation/Share";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React from "react";
import styled, { css } from "styled-components";

import {
  getSettingMultiUserGTMV2NoConstraintsAndAssign,
  getSettingMultiUserGTMV2NoConstraints,
} from "@faire/retailer/settings/getSettingMultiUserGTMV2";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { LeanInviteTeamModal } from "@faire/retailer/views/multi-user/Invite/LeanInviteTeamModal";
import {
  PageName,
  VIEW_COUNT_BEFORE_SILENCING_GTM,
} from "@faire/retailer/views/multi-user/Share/consts";
import { ShareWithTeamModal } from "@faire/retailer/views/multi-user/Share/ShareWithTeamModal";

import { ShareWithTeamGTMModal } from "./ShareWithTeamGTMModal";

interface IProps {
  type: ShareableItemType;
  token: string | undefined;
  variant: "tertiary" | "secondary";
  // The page the share button is on for event tracking purposes
  pageName: PageName;
  buttonText?: string;
  image?: IImage;
  name?: string;
  subText?: string;
  isIconButton?: boolean;
}

enum ModalType {
  NONE = "NONE",
  SHARE_MODAL = "SHARE_MODAL",
  LEAN_INVITE_MODAL = "LEAN_INVITE_MODAL",
  SHARE_MODAL_WITH_GTM = "SHARE_MODAL_WITH_GTM",
}

export const ShareButton: React.FC<IProps> = ({
  type,
  image,
  name,
  subText,
  token,
  variant,
  pageName,
  buttonText,
  isIconButton,
}) => {
  const [currentModal, setCurrentModal] = React.useState<ModalType>(
    ModalType.NONE
  );

  const { hasEverHadActiveSubUser } = useRetailerStore([
    "hasEverHadActiveSubUser",
  ]);

  const ref = useDoActionWhenVisible(() => {
    trackRetailerTeamManagementShareButtonView(pageName);
  });

  const handleShareButtonClick = () => {
    if (!hasEverHadActiveSubUser && getSettingMultiUserGTMV2NoConstraints()) {
      setCurrentModal(ModalType.SHARE_MODAL_WITH_GTM);
    } else {
      setCurrentModal(ModalType.SHARE_MODAL);
    }
    trackRetailerTeamManagementShareButtonClick(pageName);
  };

  if (
    !hasEverHadActiveSubUser &&
    !getSettingMultiUserGTMV2NoConstraintsAndAssign()
  ) {
    return null;
  }

  const showCopyLinkButton =
    type === ShareableItemType.PRODUCT || type === ShareableItemType.BRAND;

  return (
    <>
      {currentModal === ModalType.SHARE_MODAL_WITH_GTM ? (
        <ShareWithTeamGTMModal
          modalType={type}
          image={image}
          name={name}
          subText={subText}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInvite={() => setCurrentModal(ModalType.NONE)}
          pageName={pageName}
          silenceGTMMilestone="MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING"
          viewCountBeforeSilencingGTM={VIEW_COUNT_BEFORE_SILENCING_GTM}
        />
      ) : null}
      {currentModal === ModalType.SHARE_MODAL ? (
        <ShareWithTeamModal
          modalType={type}
          image={image}
          name={name}
          subText={subText}
          token={token}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInviteButtonClick={() =>
            setCurrentModal(ModalType.LEAN_INVITE_MODAL)
          }
          pageName={pageName}
          showCopyLinkButton={showCopyLinkButton}
        />
      ) : null}
      {currentModal === ModalType.LEAN_INVITE_MODAL ? (
        <LeanInviteTeamModal
          onBack={() => setCurrentModal(ModalType.SHARE_MODAL)}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInvite={() => setCurrentModal(ModalType.NONE)}
          pageName={pageName}
        />
      ) : null}
      <Row ref={ref}>
        <StyledShareButton
          buttonText={buttonText}
          variant={variant}
          isIconButton={isIconButton}
          handleShareButtonClick={handleShareButtonClick}
        />
      </Row>
    </>
  );
};

const StyledShareButton = ({
  buttonText,
  variant,
  isIconButton,
  handleShareButtonClick,
}: Pick<IProps, "buttonText" | "variant" | "isIconButton"> & {
  handleShareButtonClick: () => void;
}) => {
  const { strictLocalize } = useStrictLocalization();

  const label = strictLocalize({
    defaultMessage: "Share",
    description: { text: "Button to share with team" },
  });

  if (buttonText) {
    return (
      <StyledButtonWithText
        onClick={handleShareButtonClick}
        variant={variant}
        aria-label={label}
        data-test-id="share-icon-wrapper"
      >
        <StyledShareIcon fontSize="22px" />
        <Spacer width="1x" />
        {buttonText}
      </StyledButtonWithText>
    );
  }

  if (isIconButton) {
    return (
      <IconButton
        onClick={handleShareButtonClick}
        variant={variant}
        aria-label={label}
        data-test-id="share-icon-wrapper"
      >
        <StyledShareIcon fontSize="22px" />
      </IconButton>
    );
  }

  return (
    <StyledButton
      onClick={handleShareButtonClick}
      variant={variant}
      aria-label={label}
      data-test-id="share-icon-wrapper"
      $fixedWidth={variant === "secondary" && !buttonText}
    >
      <StyledShareIcon fontSize="22px" />
    </StyledButton>
  );
};

const StyledShareIcon = styled(Share)`
  &:hover {
    color: ${Core.icon.hover};
  }
`;

const StyledButton = styled(Button)<{ $fixedWidth?: boolean }>`
  ${({ $fixedWidth }) =>
    $fixedWidth &&
    css`
      width: 48px;
      ${responsiveMediaQueries.mobile} {
        width: 40px;
        height: 40px;
        padding: 0;
      }
    `}
`;

const StyledButtonWithText = styled(Button)`
  min-width: 180px;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
